import { AnchorHTMLAttributes, ButtonHTMLAttributes, ImgHTMLAttributes, ReactNode, useRef, useState } from 'react';
import { useSignedUrl } from '../../hooks/useSignedUrl';
import { openStoredFile, signedUrlFromStored } from '../../utils/fileUrls';

/**
 * Link, image and open-button for files kept in a PRIVATE storage bucket.
 *
 * A stored column value is no longer a usable URL — it has to be exchanged
 * for a short-lived signed link. A call site changes from
 *
 *   <a href={driver.termination_letter}>Letter</a>
 *   <img src={photo} />
 * to
 *   <SignedLink stored={driver.termination_letter}>Letter</SignedLink>
 *   <SignedImage stored={photo} />
 *
 * For a list of many files prefer useSignedUrls(), which signs them in one
 * request per bucket. `bucket` is only needed for a bare path.
 *
 * Adapted from STARBG (tmsstarbg src/components/common/SignedFile.tsx).
 */

type SignedLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  stored: string | null | undefined;
  bucket?: string;
  children: ReactNode;
  /** Shown while the link is being signed. Defaults to the children. */
  loadingContent?: ReactNode;
  /** Shown when signing failed. Defaults to a muted "Unavailable". */
  errorContent?: ReactNode;
};

/** Valid on <a>, invalid on the <span> used as its loading placeholder. */
const ANCHOR_ONLY_ATTRS = ['target', 'rel', 'download', 'ping', 'referrerPolicy', 'hrefLang', 'media', 'type'];

export function SignedLink({
  stored,
  bucket,
  children,
  loadingContent,
  errorContent,
  ...anchorProps
}: SignedLinkProps) {
  const { url, loading, error } = useSignedUrl(stored, { bucket });

  // The placeholder keeps the caller's className/title/onClick so a column of
  // links does not flash from plain text into styled links.
  const spanSafeProps = Object.fromEntries(
    Object.entries(anchorProps).filter(([key]) => !ANCHOR_ONLY_ATTRS.includes(key))
  );

  if (!stored) return null;

  if (loading) {
    return (
      <span
        {...spanSafeProps}
        className={`${anchorProps.className ?? ''} opacity-60 cursor-wait`.trim()}
        aria-busy="true"
      >
        {loadingContent ?? children}
      </span>
    );
  }

  if (error || !url) {
    return (
      <span className="text-gray-400" title="This file could not be opened">
        {errorContent ?? 'Unavailable'}
      </span>
    );
  }

  return (
    <a href={url} target="_blank" rel="noopener noreferrer" {...anchorProps}>
      {children}
    </a>
  );
}

type SignedImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> & {
  stored: string | null | undefined;
  bucket?: string;
  /** Rendered in place of the image while signing or on failure. */
  fallback?: ReactNode;
};

export function SignedImage({ stored, bucket, fallback, className, ...imgProps }: SignedImageProps) {
  const { url, loading, error } = useSignedUrl(stored, { bucket });
  // A signed URL lasts an hour. A tab left open past that shows the broken
  // image glyph, so retry once with a fresh signature before giving up.
  // Retry state is keyed by the file it belongs to: when a lightbox moves from
  // photo A to photo B, A's refreshed URL must not render under B's label,
  // not even for one frame.
  const [retriedFor, setRetriedFor] = useState<string | null>(null);
  const [retry, setRetry] = useState<{ for: string; url: string } | null>(null);
  const [failedFor, setFailedFor] = useState<string | null>(null);
  // The CURRENT subject, readable from an async handler that closed over an
  // older render's `stored`.
  const storedRef = useRef(stored);
  storedRef.current = stored;
  const retryUrl = stored && retry?.for === stored ? retry.url : null;
  const failed = !!stored && failedFor === stored;

  // onClick/style must survive the placeholder states, and the caller's
  // onError is composed with the retry rather than replacing it.
  const { onClick, style, onError: callerOnError, ...restImgProps } = imgProps;

  if (!stored) return <>{fallback ?? null}</>;

  if (loading) {
    return (
      <div
        className={`bg-gray-100 animate-pulse ${className ?? ''}`}
        style={style}
        onClick={onClick as never}
        aria-busy="true"
        aria-label="Loading image"
      />
    );
  }

  if (error || failed || !url) {
    return (
      <>
        {fallback ?? (
          <div
            className={`bg-gray-100 flex items-center justify-center text-xs text-gray-400 ${className ?? ''}`}
            style={style}
            onClick={onClick as never}
            title="This image could not be loaded"
          >
            Unavailable
          </div>
        )}
      </>
    );
  }

  return (
    <img
      src={retryUrl ?? url}
      className={className}
      style={style}
      onClick={onClick}
      onError={async e => {
        callerOnError?.(e);
        const retryingFor = stored;
        // The fresh signature failed to load too: show Unavailable rather
        // than leave the browser's broken-image glyph on screen.
        if (retriedFor === retryingFor) {
          setFailedFor(retryingFor);
          return;
        }
        setRetriedFor(retryingFor);
        const fresh = await signedUrlFromStored(retryingFor, { bucket, force: true });
        // The caller moved on to another file while this was in flight.
        if (retryingFor !== storedRef.current) return;
        if (fresh) setRetry({ for: retryingFor, url: fresh });
        else setFailedFor(retryingFor);
      }}
      {...restImgProps}
    />
  );
}

/**
 * Button that opens a stored file in a new tab, signing it on click. Use in
 * tables and anywhere a link may sit open longer than the one-hour signature:
 * it removes both the expiry cliff and the request-per-row cost.
 */
type SignedOpenButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> & {
  stored: string | null | undefined;
  bucket?: string;
  children: ReactNode;
  onUnavailable?: () => void;
};

export function SignedOpenButton({ stored, bucket, children, onUnavailable, ...buttonProps }: SignedOpenButtonProps) {
  return (
    <button
      type="button"
      onClick={async () => {
        const opened = await openStoredFile(stored, { bucket });
        if (!opened) onUnavailable?.();
      }}
      {...buttonProps}
    >
      {children}
    </button>
  );
}
